import React, { useEffect, useMemo, useState } from "react"

import { useForm } from "react-hook-form"

import { FeatureFlag } from "@common-shared/config/featureFlags.config"
import { OneVerifyApplicationRole } from "@common-shared/config/rolesPermissions.config"
import { IOneVerifyUserInviteDataNoBillerGroups } from "@common-shared/types/requestData/oneVerifyRequestData.types"
import { IOneVerifyBillerGroup } from "@common-shared/types/responseData/oneVerifyResponseData.types"

import { FRONT_VALIDATION_CONFIG } from "@front-shared/config/validation.config"
import { ButtonElement } from "@front-shared/elements/button.element"
import { InputElement } from "@front-shared/elements/input.element"
import { PopupWrapperElement } from "@front-shared/elements/popupWrapper.element"
import { PrimaryText } from "@front-shared/elements/primaryText.element"
import { SelectElement } from "@front-shared/elements/select.element"
import { ConfirmationHelper } from "@front-shared/helpers/confirmation.helper"
import { useStateCallback } from "@front-shared/hooks/useStateCallback.hook"
import type { ISelectRenderedOption } from "@front-shared/types/objects.types"
import type { IPopupProps } from "@front-shared/types/props.types"

import { ROLES_PERMISSIONS_CONFIG } from "../../config/rolesPermissions.config"
import { OrganizationInviteHelper } from "../../helpers/organizationInvite.helper"
import { RuntimeHelper } from "../../helpers/runtime.helper"
import { UtilHelper } from "../../helpers/util.helper"
import { useIsFeatureFlagEnabled } from "../../hooks/isFeatureFlagEnabled.hook"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { billerGroupsSelector } from "../../store/selectors/billerGroups.selector"
import { runtimeSelector } from "../../store/selectors/runtime.selector"
import { selectedPracticeConfigurationSelector } from "../../store/selectors/selectedPracticeConfiguration.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { billerGroupsGetList } from "../../store/thunks/billerGroups.thunks"
import { organizationInvitesCreate } from "../../store/thunks/organization.thunks"

import closeIcon from "@front-shared/assets/images/icons/close-dark.svg"
import mailIcon from "@front-shared/assets/images/icons/mail-dark.svg"

export const OrganizationUserInvitePopupComponent: React.FunctionComponent<
    IPopupProps
> = props => {
    const dispatch = useAppDispatch()

    const {
        handleSubmit,
        formState: { errors },
        control,
        setValue,
    } = useForm()

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)
    const runtimeState = useAppSelector(runtimeSelector)
    const billerGroups = useAppSelector(billerGroupsSelector)
    const selectedPracticeConfiguration = useAppSelector(
        selectedPracticeConfigurationSelector,
    )

    const [selectedBillerGroups, setSelectedBillerGroups] = useStateCallback<
        IOneVerifyBillerGroup[]
    >([])

    const [
        hideInviteModalForDomainConfirmation,
        setHideInviteModalForDomainConfirmation,
    ] = useState(false)

    const isLoading = useMemo<boolean>(
        () => RuntimeHelper.isOrganizationInviteBeingSent(),
        [runtimeState.isLoading],
    )

    const isBillerGroupsEnabled = useIsFeatureFlagEnabled(
        FeatureFlag.ONE_VERIFY_BILLER_GROUPS,
    )

    const getBillerGroupDisplayValue = (
        group: IOneVerifyBillerGroup,
    ): string => {
        return group.name || group.id.toString()
    }

    const availableBillerGroupsOptions = useMemo<
        ISelectRenderedOption[]
    >(() => {
        return billerGroups.items.map(item => ({
            displayValue: getBillerGroupDisplayValue(item),
            value: item,
        }))
    }, [billerGroups.items])

    const onBillerGroupSelected = (group: IOneVerifyBillerGroup) => {
        setSelectedBillerGroups(current => [group, ...current])
    }

    const onBillerGroupDeselected = (billerGroupDisplayValue: string) => {
        setSelectedBillerGroups(current =>
            current.filter(
                group =>
                    billerGroupDisplayValue !==
                    getBillerGroupDisplayValue(group),
            ),
        )
    }

    // Preload biller groups on mount
    useEffect(() => {
        if (!selectedPracticeRole || !isBillerGroupsEnabled) {
            return
        }

        dispatch(
            billerGroupsGetList({
                payload: UtilHelper.getPagination(100),
                practice: selectedPracticeRole.practice,
            }),
        )
    }, [selectedPracticeRole?.id, isBillerGroupsEnabled])

    useEffect(() => {
        if (!props.isActive) {
            setHideInviteModalForDomainConfirmation(false)
        }
    }, [props.isActive])

    const onSave = (data: any) => {
        if (!selectedPracticeRole || !selectedPracticeConfiguration) {
            return
        }

        const inputData = data as IOneVerifyUserInviteDataNoBillerGroups
        const practice = selectedPracticeRole.practice

        const sendInvite = (domainConfirmed?: boolean) => {
            dispatch(
                organizationInvitesCreate({
                    practice,
                    payload: {
                        role: inputData.role,
                        email: inputData.email,
                        billerGroupIds: selectedBillerGroups.map(
                            item => item.id,
                        ),
                        ...(domainConfirmed ? { domainConfirmed: true } : {}),
                    },
                    onSuccess: props.onDone,
                    onError: () => {
                        if (domainConfirmed) {
                            setHideInviteModalForDomainConfirmation(false)
                        }
                    },
                }),
            )
        }

        if (
            OrganizationInviteHelper.doesInviteRequireDomainConfirmation(
                selectedPracticeConfiguration,
                inputData.email,
            )
        ) {
            setHideInviteModalForDomainConfirmation(true)

            window.setTimeout(() => {
                ConfirmationHelper.enableConfirmation(
                    () => {
                        sendInvite(true)
                    },
                    "Confirm invite",
                    OrganizationInviteHelper.buildInviteOutsideAllowedDomainsConfirmationLines(
                        {
                            email: inputData.email,
                            roleLabel:
                                OrganizationInviteHelper.getInviteRoleLabelForConfirmation(
                                    inputData.role,
                                ),
                            groupsDisplay:
                                selectedBillerGroups.length > 0
                                    ? selectedBillerGroups
                                          .map(
                                              item =>
                                                  item.name || String(item.id),
                                          )
                                          .join(", ")
                                    : "None",
                            allowedDomains:
                                selectedPracticeConfiguration.allowedUserAccountDomains,
                        },
                    ),
                    () => {
                        setHideInviteModalForDomainConfirmation(false)
                    },
                )
            }, 0)

            return
        }

        sendInvite()
    }

    return (
        <PopupWrapperElement
            isActive={props.isActive && !hideInviteModalForDomainConfirmation}
            className={props.className}
            onDone={props.onDone}
            closeOnOutsideClick={props.closeOnOutsideClick}
            popupWidthClassName="w-[720px] !rounded-[24px] md:w-screen"
        >
            <div className="flex items-center">
                <PrimaryText typography="h3" className="flex-1 mr-[24px]">
                    Add User
                </PrimaryText>

                <div className="cursor-pointer" onClick={props.onDone}>
                    <img src={closeIcon} alt="Close" title="Close" />
                </div>
            </div>

            <InputElement
                className="mt-[52px]"
                name="email"
                label="Email"
                reactHookControl={control}
                reactHookErrors={errors}
                reactHookValidations={{
                    required: FRONT_VALIDATION_CONFIG.required,
                    pattern: FRONT_VALIDATION_CONFIG.email,
                    maxLength: FRONT_VALIDATION_CONFIG.maxLength,
                }}
                inputSuffix={
                    <img
                        src={mailIcon}
                        alt="Email address"
                        title="Email address"
                        className="ml-[5px]"
                    />
                }
            />

            <SelectElement
                className="mt-[52px]"
                placeholder="Select one"
                label="Role"
                name="renderedRole"
                reactHookFormControl={control}
                validations={{
                    required: FRONT_VALIDATION_CONFIG.required,
                }}
                renderedOptions={ROLES_PERMISSIONS_CONFIG.inviteRenderedRoles}
                onSelected={(role: OneVerifyApplicationRole) => {
                    setValue("role", role)
                }}
            />

            {isBillerGroupsEnabled ? (
                <SelectElement
                    className="mt-[52px]"
                    name="billerGroups"
                    mode="multiple"
                    label="Groups"
                    renderedOptions={availableBillerGroupsOptions}
                    onSelected={onBillerGroupSelected}
                    onDeselected={onBillerGroupDeselected}
                />
            ) : null}

            <div className="mt-[32px] flex">
                <div className="flex-1" />

                <ButtonElement
                    label="Send invite"
                    type="primary"
                    size="large"
                    htmlType="button"
                    onClick={handleSubmit(onSave)}
                    isLoading={isLoading}
                />
            </div>
        </PopupWrapperElement>
    )
}
